if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.viewer) return;

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (button) {
                button.addEventListener(
                  'click',
                  this.setActiveMedia.bind(
                    this,
                    mediaToSwitch.dataset.target,
                    false
                  )
                );
              }
            });
        }

        if (
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }

        /*
         * Watch for Shopify variant changes.
         */
        document.addEventListener(
          'change',
          this.handleVariantChange.bind(this)
        );

        /*
         * Apply the filter on initial page load.
         */
        setTimeout(() => {
          this.filterGalleryByColor();
        }, 100);
      }

      /*
       * Find the selected Colour value.
       */
      getSelectedColor() {
        const colorNames = [
          'colour',
          'color',
        ];

        /*
         * Shopify Horizon/Dawn-style variant selectors
         */
        const fieldsets = document.querySelectorAll(
          'variant-selects fieldset'
        );

        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');

          if (!legend) continue;

          const optionName = legend.textContent
            .trim()
            .toLowerCase()
            .replace(/:$/, '');

          if (!colorNames.includes(optionName)) continue;

          const selected = fieldset.querySelector(
            'input[type="radio"]:checked'
          );

          if (selected) {
            return selected.value.trim();
          }

          const select = fieldset.querySelector('select');

          if (select) {
            return select.value.trim();
          }
        }

        /*
         * Fallback for select-based variant pickers.
         */
        const selects = document.querySelectorAll(
          'variant-selects select'
        );

        for (const select of selects) {
          const label = select
            .closest('.product-form__input')
            ?.querySelector('label');

          if (!label) continue;

          const optionName = label.textContent
            .trim()
            .toLowerCase()
            .replace(/:$/, '');

          if (!colorNames.includes(optionName)) continue;

          return select.value.trim();
        }

        return null;
      }

      /*
       * Convert:
       * "White Gold"
       * into:
       * "white-gold"
       */
      normalizeColor(value) {
        return value
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }

      /*
       * Get the grouping tag from an image ALT.
       *
       * Example:
       * color:white-gold
       *
       * returns:
       * white-gold
       */
      getMediaColor(mediaElement) {
        const image = mediaElement.querySelector('img');

        if (!image) return null;

        const alt = (image.getAttribute('alt') || '').trim();

        /*
         * Blank ALT = always visible.
         */
        if (!alt) return null;

        const match = alt.match(/(?:^|\s)color:([a-z0-9-]+)/i);

        if (!match) return null;

        return match[1].toLowerCase();
      }

      /*
       * Hide/show gallery images and thumbnails.
       */
      filterGalleryByColor() {
        const selectedColor = this.getSelectedColor();

        if (!selectedColor) return;

        const normalizedColor =
          this.normalizeColor(selectedColor);

        /*
         * MAIN GALLERY
         */
        const slides =
          this.elements.viewer.querySelectorAll(
            '[data-media-id]'
          );

        let visibleSlides = [];

        slides.forEach((slide) => {
          const mediaColor =
            this.getMediaColor(slide);

          /*
           * No color tag = ALWAYS VISIBLE.
           */
          const shouldShow =
            !mediaColor ||
            mediaColor === normalizedColor;

          slide.hidden = !shouldShow;

          if (shouldShow) {
            visibleSlides.push(slide);
          }

          if (!shouldShow) {
            slide.classList.remove('is-active');
          }
        });

        /*
         * THUMBNAILS
         */
        if (this.elements.thumbnails) {
          const thumbnails =
            this.elements.thumbnails.querySelectorAll(
              '[data-target]'
            );

          thumbnails.forEach((thumbnail) => {
            const mediaId =
              thumbnail.dataset.target;

            const media =
              this.elements.viewer.querySelector(
                `[data-media-id="${mediaId}"]`
              );

            if (!media) return;

            thumbnail.hidden = media.hidden;
          });
        }

        /*
         * Make the first visible image active.
         */
        const activeSlide =
          this.elements.viewer.querySelector(
            '[data-media-id]:not([hidden])'
          );

        if (activeSlide) {
          slides.forEach((slide) => {
            slide.classList.remove('is-active');
          });

          activeSlide.classList.add('is-active');

          /*
           * Update thumbnails.
           */
          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${activeSlide.dataset.mediaId}"]`
              );

            if (activeThumbnail) {
              this.setActiveThumbnail(
                activeThumbnail
              );
            }
          }

          /*
           * Reset slider pages so hidden slides
           * don't interfere with navigation.
           */
          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }
      }

      /*
       * Detect Shopify variant/option changes.
       */
      handleVariantChange(event) {
        const target = event.target;

        if (!target) return;

        /*
         * Only react to variant controls.
         */
        const isVariantControl =
          target.matches(
            'variant-selects select, variant-radios input, input[name^="options"], select[name^="options"]'
          );

        if (!isVariantControl) return;

        /*
         * Give Shopify time to update
         * the selected variant.
         */
        setTimeout(() => {
          this.filterGalleryByColor();
        }, 100);
      }

      onSlideChanged(event) {
        const thumbnail =
          this.elements.thumbnails?.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]:not([hidden])`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]:not([hidden])'
          );

        if (!activeMedia) {
          return;
        }

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail &&
                activeThumbnail.parentElement.prepend(
                  activeThumbnail
                );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top: top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) =>
            element.removeAttribute('aria-current')
          );

        const button =
          thumbnail.querySelector('button');

        if (!button) return;

        button.setAttribute(
          'aria-current',
          true
        );

        if (
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image =
          activeItem.querySelector(
            '.product__modal-opener--image img'
          );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) =>
            slide.setAttribute(
              'role',
              'presentation'
            )
        );
      }
    }
  );
}